import i18n from '@/plugins/vue-i18n';
import { AxiosError } from 'axios';
import { useToastStore } from '@/stores/utils/toast.store';
import { ErrorResponse } from '@/types/api/response.types';
import { useVendorStore } from '@/stores/vendors.store';
import { useDrawStore } from '@/stores/draws.store';
import { useAuthStore } from '@/stores/auth.store';
import { EMAIL_NOT_VERIFIED } from '@/common/utils/http.utils';

const { t } = i18n.global;
const errorMapper = {
  catchAll: {
    title: t('error.toast.validation.catchAll.title'),
    description: t('error.toast.validation.catchAll.description'),
  },
  invalidCredentials: {
    title: t('error.toast.validation.invalidCredentials.title'),
    description: t('error.toast.validation.invalidCredentials.description'),
  },
  validationFailed: {
    title: t('error.toast.validation.validationFailed.title'),
    description: t('error.toast.validation.validationFailed.description'),
  },
  userAlreadyExists: {
    title: t('error.toast.validation.emailAlreadyExists.title'),
    description: t('error.toast.validation.emailAlreadyExists.description'),
  },
  vendorRemoved: {
    title: t('error.toast.validation.vendorRemoved.title'),
  },
  invalidItem: {
    title: t('error.toast.validation.invalidItem'),
  },
  invalidSpec: {
    title: t('error.toast.validation.invalidSpec'),
  },
  orderItemVariation: {
    title: t('error.toast.validation.orderItemVariation.title'),
    description: t('error.toast.validation.orderItemVariation.description'),
  },
  ruleViolation: {
    title: t('error.toast.validation.ruleViolation.title'),
    description: t('error.toast.validation.ruleViolation.description'),
  },
  roleAlreadyAssigned: {
    title: t('error.toast.validation.roleAlreadyAssigned.title'),
    description: t('error.toast.validation.roleAlreadyAssigned.description'),
  },
  imageUploadFailed: {
    title: t('error.toast.validation.imageUploadFailed.title'),
    description: t('error.toast.validation.imageUploadFailed.description'),
  },
};

/**
 * Org-user role add/remove endpoints: `/organisations/{id}/users/{userId}/roles[/{code}]`.
 */
const isOrgUserRoleEndpoint = (url?: string): boolean =>
  !!url && /\/organisations\/[^/]+\/users\/[^/]+\/roles(\/|$)/.test(url);

export const processCustomError = (_error: AxiosError<ErrorResponse>): void => {
  const toastService = useToastStore();
  const vendorStore = useVendorStore();
  const drawStore = useDrawStore();

  // Context: sign-in owns this one — it routes the user to the resend-confirmation step rather
  // than telling them off, so a toast here would be a dead end alongside the fix (TCH-469).
  if ((_error.response?.data?.meta as { code?: string } | undefined)?.code === EMAIL_NOT_VERIFIED) {
    return;
  }

  // A 409 on the org-user role endpoints means the role is already assigned —
  // not an optimistic-concurrency version conflict. Surface it as a plain toast.
  if (_error.response?.status === 409 && isOrgUserRoleEndpoint(_error.config?.url)) {
    return toastService.showToast(
      'error',
      errorMapper.roleAlreadyAssigned.title,
      errorMapper.roleAlreadyAssigned.description,
    );
  }

  if (
    _error.response?.status === 403 &&
    _error.config?.method !== 'delete' &&
    (_error.config?.url?.includes('/logos') || _error.config?.url?.includes('/images'))
  ) {
    return toastService.showToast(
      'error',
      errorMapper.imageUploadFailed.title,
      errorMapper.imageUploadFailed.description,
    );
  }

  if (
    _error.config?.method === 'get' &&
    _error.config?.url?.includes('/auth/invitations') &&
    _error.response?.status === 404
  ) {
    const authStore = useAuthStore();
    authStore.inviteInvalid = true;
    return;
  }

  if (
    _error.config?.method === 'post' &&
    _error.config?.url?.includes('/draws/') &&
    _error.config?.url?.includes('/claims')
  ) {
    drawStore.toggleLoadingModal(false);
    return toastService.showToast(
      'error',
      t('sites.claims.errorToast.title'),
      t('sites.claims.errorToast.content'),
    );
  }

  if (_error.response?.data?.meta?.message?.includes('Email or password could not be validated.')) {
    return toastService.showToast(
      'error',
      errorMapper.invalidCredentials.title,
      errorMapper.invalidCredentials.description,
    );
  }

  if (_error.response?.data?.errors) {
    if (
      _error.response.data.errors[0]?.displayMessage?.includes(
        'A user with this email already exists.',
      )
    ) {
      return toastService.showToast(
        'error',
        errorMapper.userAlreadyExists.title,
        errorMapper.userAlreadyExists.description,
      );
    }

    if (
      _error.response.data.errors[0]?.displayMessage?.includes('File header fields do not match')
    ) {
      vendorStore.bulkUploadInvalidFile = true;
      return;
    }

    if (_error.response.data.errors[0]?.errorType === 'RuleViolation') {
      const errorMessage = _error.response.data.errors[0]?.displayMessage
        ? _error.response.data.errors[0].displayMessage.trim()
        : errorMapper.catchAll.title;

      return toastService.showToast('error', errorMapper.ruleViolation.title, errorMessage);
    }

    if (
      _error.response.data.errors[0]?.displayMessage?.includes(
        'This item in the BOQ already has a pending variation. This must first be approved or denied before a new variation can be created.',
      )
    ) {
      return;
    }

    if (_error.response.data.errors[0]?.displayMessage?.includes('Token validation failed.')) {
      return toastService.showToast(
        'error',
        errorMapper.validationFailed.title,
        errorMapper.validationFailed.description,
      );
    }

    if (
      /(can not be moved|marked Live|marked live)/.test(
        _error.response.data.errors[0]?.displayMessage ?? '',
      )
    ) {
      return toastService.showToast(
        'error',
        errorMapper.catchAll.title,
        _error.response?.data?.errors[0]?.displayMessage ?? '',
      );
    }

    if (_error.response.data.errors[0]?.field === 'Vendor') {
      return toastService.showToast(
        'error',
        errorMapper.vendorRemoved.title,
        _error.response.data.errors[0]?.displayMessage,
      );
    }

    if (_error.response.data.errors[0]?.field === 'Specification') {
      return toastService.showToast(
        'error',
        errorMapper.invalidItem.title,
        _error.response.data.errors[0]?.displayMessage,
      );
    }
    if (_error.response.data.errors[0]?.errorType === 'InvalidReference') {
      return toastService.showToast(
        'error',
        errorMapper.invalidSpec.title,
        _error.response.data.errors[0]?.displayMessage,
      );
    }
  }

  // Context: a typed refusal says what is in the way in `meta.message` and carries no `errors[]`.
  // Only bulk orders send one today; a bulk site order's return is refused this way while a run
  // holds its bulk order, so the Orders tab meets it too (PE-1049).
  const meta = _error.response?.data?.meta;
  const typedRefusal = _error.response?.status === 400 && meta?.kind ? meta.message : undefined;

  return toastService.showToast(
    'error',
    errorMapper.catchAll.title,
    _error.response
      ? (_error.response?.data?.errors?.[0]?.displayMessage ?? typedRefusal ?? '')
      : errorMapper.catchAll.description,
  );
};
